import { useState, useEffect, useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import Papa from 'papaparse';
import { MouseEvent } from 'react';
import { saveAs } from 'file-saver';
import { Download, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import Feedback from 'src/features/plugins/outlook/components/Feedback';
import LoadingPage from 'src/features/plugins/outlook/components/LoadingPage';
import CopyButton from 'src/features/plugins/outlook/components/CopyButton';
import { CsvTableV2 } from 'src/features/plugins/outlook/components/CsvtableV2';
import { Separator } from 'src/shared/components/ui/separator';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { useToast } from 'src/shared/components/ui/use-toast';
import {
  ApprovalData,
  PUSH_CSV_FILE_TO_OUTLOOK_APP,
  PushCsvFileToOutlookAppData,
} from 'src/features/flows/types/threadStore.types';
import { useThreadStores } from 'src/features/flows/queries/useThreadStores';
import { ButtonWithActions } from 'src/shared/components/ui/buttons/button-with-actions';
import SaveButton from 'src/shared/components/ui/buttons/save-button';
import { useMutateThreadStore } from 'src/features/flows/queries/useMutateThreadStore';
import { useSocket } from 'src/shared/hooks/useSocket';
import { genQueryKey } from 'src/shared/lib/query.utils';

function NoDataFeedback() {
  return (
    <Feedback testId="outlook-show-file-no-data" className="w-[300px]">
      <div className="flex flex-col justify-center w-full gap-2">
        <Feedback.Title text="No data" />
        <Separator className="mb-1" />
        <Feedback.Description text="This file does not contain any data to show." />
        <Feedback.Action text="Close window" onClick={() => Office.context.ui.messageParent('close')} />
      </div>
    </Feedback>
  );
}

export default function ShowFilePage() {
  const searchParams = useSearch({ strict: false }) as Record<string, string | undefined>;
  const integrationId = searchParams['integration'];
  const threadId = searchParams['thread'];
  const threadStoreId = searchParams['threadStore'] ?? null;
  const valueToApproveId = searchParams['valueToApproveId'] ?? null;

  const { data, isPending } = useThreadStores(integrationId!, threadId!);
  const { updateThreadStore } = useMutateThreadStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentOrg } = useOrg();

  // Subscribe to websocket room for this thread
  const roomId = useMemo(() => {
    if (integrationId && threadId) {
      return ['thread', integrationId, threadId];
    }
    return [];
  }, [integrationId, threadId]);

  // Listen for thread_store events
  useSocket({
    roomId,
    event: 'thread_store',
    disabled: !integrationId || !threadId || !currentOrg,
    callback: () => {
      // Invalidate threadStores query when thread_store event is received
      if (integrationId && threadId && currentOrg) {
        const threadStoresQueryKey = ['thread-stores', genQueryKey(currentOrg.id, integrationId, threadId)];
        queryClient.invalidateQueries({ queryKey: threadStoresQueryKey });
      }
    },
  });

  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [file, setFile] = useState<
    { file: string; filename?: string; containsHeader: boolean; title: string } | undefined
  >(undefined);
  const { orgFetch } = useOrg();

  const threadStore = useMemo(() => data?.find((t) => t.id === threadStoreId), [data, threadStoreId]);
  const editable = useMemo(
    () =>
      !!valueToApproveId &&
      threadStore?.type === 'approval' &&
      ['pending', 'failed'].includes(threadStore?.data.status),
    [valueToApproveId, threadStore]
  );

  // Update file state when threadStore changes (e.g., after websocket refetch)
  useEffect(() => {
    if (!threadStore) return;

    if (threadStore.type === PUSH_CSV_FILE_TO_OUTLOOK_APP) {
      const csvData = threadStore.data as PushCsvFileToOutlookAppData;
      setFile({
        file: csvData.file,
        filename: csvData.filename,
        containsHeader: csvData.containsHeader,
        title: csvData.title,
      });
    } else if (threadStore.type === 'approval' && valueToApproveId) {
      const record = (threadStore.data as ApprovalData).valuesToApprove[valueToApproveId];
      if (record) {
        setFile({
          file: record.value,
          filename: 'approval-value.csv',
          containsHeader: record.containsHeader,
          title: record.label,
        });
      }
    }
  }, [threadStore, valueToApproveId]);

  // Parse CSV when file data changes
  useEffect(() => {
    if (!file) return;

    // Validate that file.file is a string and not undefined
    if (!file.file || typeof file.file !== 'string') {
      setHeaders([]);
      setDataRows([]);
      setDelimiter(',');
      return;
    }

    try {
      // Use PapaParse to properly handle quoted fields and escaped characters
      const parsed = Papa.parse<string[]>(file.file, {
        header: false,
        skipEmptyLines: true,
      });

      const csvDelimiter = parsed?.meta?.delimiter || ',';
      setDelimiter(csvDelimiter);

      const allRows = parsed.data;

      if (allRows.length > 0) {
        if (file.containsHeader) {
          setHeaders(allRows[0]);
          setDataRows(allRows.slice(1));
        } else {
          setHeaders([]);
          setDataRows(allRows);
        }
      } else {
        setHeaders([]);
        setDataRows([]);
        setDelimiter(',');
      }
    } catch (error) {
      console.error('Error parsing CSV in show-file page:', error);
      setHeaders([]);
      setDataRows([]);
      setDelimiter(',');
    }
  }, [file?.file, file?.containsHeader]);

  if (isPending) {
    return <LoadingPage testId="outlook-show-file-loading" />;
  }

  if (!threadStoreId || !threadStore) {
    console.error('Thread store not found');
    return <NoDataFeedback />;
  }

  if (valueToApproveId) {
    const valueToApprove = data?.find((t) => t.type === 'approval' && t.data.valuesToApprove[valueToApproveId]);
    if (!valueToApprove) {
      return <NoDataFeedback />;
    }
  } else if (threadStore.type !== PUSH_CSV_FILE_TO_OUTLOOK_APP) {
    return (
      <Feedback testId="outlook-show-file-unsupported" className="w-[300px]">
        <div className="flex flex-col justify-center w-full gap-2">
          <Feedback.Title text="Unsupported file type" />
          <Separator className="mb-1" />
          <Feedback.Description text="Sorry, we can't show a preview of this file type." />
          <Feedback.Action text="Close window" onClick={() => Office.context.ui.messageParent('close')} />
        </div>
      </Feedback>
    );
  }

  if (!file) {
    return <NoDataFeedback />;
  }

  const handleDownload = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const blob = new Blob([file.file], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, file.filename ?? file.title);
  };

  function handleReconstructCsv(): string {
    // Use Papa.unparse for proper CSV escaping (handles fields with delimiters, quotes, newlines)
    const allRows = file?.containsHeader ? [headers, ...dataRows] : dataRows;
    return Papa.unparse(allRows, { delimiter });
  }

  async function approve() {
    try {
      if (!threadStore) {
        toast({
          title: 'Error',
          description: 'Failed to approve',
          variant: 'error',
        });
        throw new Error('Thread store not found');
      }
      const res = await orgFetch<any>(
        `/api/flow/v2/${threadStore?.flowId}/approval/${(threadStore?.data as ApprovalData).approvalId}?result=approved`,
        {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        toast({
          title: 'Error',
          description: 'Failed to approve',
          variant: 'error',
        });
        throw new Error('Failed to approve');
      }
    } catch (err) {
      console.error(err);
      toast({
        title: 'Error',
        description: 'Failed to approve',
        variant: 'error',
      });
      throw err;
    }
  }

  return dataRows?.length > 0 ? (
    <div data-testid="outlook-show-file-content" className="w-[100vw] min-h-[100vh] bg-white flex flex-col">
      <div className="flex p-3 items-center shrink-0">
        <div className="flex gap-2">
          <span className="font-semibold text-sm">{file.title}</span>
          <CopyButton variant="subtle" value={file.file} />
        </div>
        <div className="flex-grow" />
        {Office.context.platform !== Office.PlatformType.Mac && (
          <div
            className="mr-3 text-typography-grey hover:underline underline-offset-2 hover:text-typography-black transition-all text-xs font-light flex items-center gap-1.5 cursor-pointer"
            onClick={handleDownload}
          >
            <Download size={12} />
            Download
          </div>
        )}
        <div
          className="mr-3 text-typography-grey hover:underline underline-offset-2 hover:text-typography-black transition-all text-xs font-light flex items-center gap-1.5 cursor-pointer"
          onClick={() => Office.context.ui.messageParent('close')}
        >
          <X size={12} />
          Close
        </div>
      </div>
      <Separator className="shrink-0" />
      <div className="w-full bg-white p-4">
        <CsvTableV2 headers={headers} rows={dataRows} editable={editable} onDataChange={setDataRows} />
      </div>
      {editable && (
        <>
          <Separator className="shrink-0" />
          <div className="flex w-full justify-end px-5 py-2 gap-2 shrink-0">
            <SaveButton variant="outline" loading={false} onClick={() => Office.context.ui.messageParent('close')}>
              Cancel
            </SaveButton>
            <ButtonWithActions
              actions={[
                {
                  label: 'Save',
                  onClick: async () => {
                    await updateThreadStore.mutateAsync({
                      threadStoreId,
                      valuesToApprove: { [valueToApproveId!]: handleReconstructCsv() },
                    });
                    Office.context.ui.messageParent('close');
                  },
                },
              ]}
              onMainAction={async () => {
                await updateThreadStore.mutateAsync({
                  threadStoreId,
                  valuesToApprove: { [valueToApproveId!]: handleReconstructCsv() },
                });
                await approve();
                Office.context.ui.messageParent('close');
              }}
            >
              Save and approve
            </ButtonWithActions>
          </div>
        </>
      )}
    </div>
  ) : (
    <NoDataFeedback />
  );
}
